import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import api from '../../api/axios';

const AdminQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    option_e: '',
    option_f: '',
    correct_answer: '',
    explanation: '',
    mode: 'MC',
    topic: ''
  });

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/questions.php?limit=1000');
      setQuestions(res.data);
    } catch (err) {
      setError('Failed to load questions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (q) => {
    setEditingId(q.id);
    setFormData({
      question_text: q.question_text || '',
      option_a: q.option_a || '',
      option_b: q.option_b || '',
      option_c: q.option_c || '',
      option_d: q.option_d || '',
      option_e: q.option_e || '',
      option_f: q.option_f || '',
      correct_answer: q.correct_answer || '',
      explanation: q.explanation || '',
      mode: q.mode || 'MC',
      topic: q.topic || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      await api.delete(`/questions.php?id=${id}`);
      fetchQuestions();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleNew = () => {
    setEditingId(null);
    setFormData({
      question_text: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      option_e: '',
      option_f: '',
      correct_answer: '',
      explanation: '',
      mode: 'MC',
      topic: ''
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.question_text || !formData.correct_answer) {
      alert('Question text and correct answer are required');
      return;
    }
    try {
      if (editingId) {
        await api.put(`/questions.php?id=${editingId}`, formData);
      } else {
        await api.post('/questions.php', formData);
      }
      setShowModal(false);
      fetchQuestions();
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return <Container className="mt-4 text-center"><Spinner animation="border" /></Container>;

  return (
    <Container className="mt-4" style={{ maxWidth: '1400px' }}>
      <h2 className="mb-3">Manage Questions</h2>
      <Button variant="primary" onClick={handleNew} className="mb-3">
        + Add New Question
      </Button>
      <Button variant="outline-primary" className="mb-3 ms-2" onClick={() => window.history.back()}>
        Back
      </Button>
      {error && <Alert variant="danger">{error}</Alert>}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>Question</th>
            <th>Mode</th>
            <th>Topic</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {questions.map(q => (
            <tr key={q.id}>
              <td>{q.id}</td>
              <td>{q.question_text?.substring(0, 80)}...</td>
              <td><Badge bg={q.mode === 'MC' ? 'primary' : 'info'}>{q.mode}</Badge></td>
              <td>{q.topic || '—'}</td>
              <td>
                <Button size="sm" variant="warning" onClick={() => handleEdit(q)} className="me-2">
                  Edit
                </Button>
                <Button size="sm" variant="danger" onClick={() => handleDelete(q.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit Question' : 'Add New Question'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Question Text</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="question_text"
                value={formData.question_text}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Option A</Form.Label>
                  <Form.Control
                    type="text"
                    name="option_a"
                    value={formData.option_a}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Option B</Form.Label>
                  <Form.Control
                    type="text"
                    name="option_b"
                    value={formData.option_b}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Option C</Form.Label>
                  <Form.Control
                    type="text"
                    name="option_c"
                    value={formData.option_c}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Option D</Form.Label>
                  <Form.Control
                    type="text"
                    name="option_d"
                    value={formData.option_d}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Option E (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="option_e"
                    value={formData.option_e}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Option F (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="option_f"
                    value={formData.option_f}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Correct Answer(s)</Form.Label>
                  <Form.Control
                    type="text"
                    name="correct_answer"
                    value={formData.correct_answer}
                    onChange={handleChange}
                    placeholder="e.g., A or A,C,D"
                    required
                  />
                  <Form.Text className="text-muted">
                    For SATA, use commas (e.g., A,C,E)
                  </Form.Text>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Mode</Form.Label>
                  <Form.Select name="mode" value={formData.mode} onChange={handleChange}>
                    <option value="MC">Multiple Choice</option>
                    <option value="SATA">Select All That Apply</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Topic</Form.Label>
                  <Form.Control
                    type="text"
                    name="topic"
                    value={formData.topic}
                    onChange={handleChange}
                    placeholder="e.g., Maternal/Newborn"
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Explanation</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="explanation"
                value={formData.explanation}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {editingId ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminQuestions;